/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import isEqual from 'lodash/fp/isEqual.js';
import set from 'lodash/fp/set.js';
import {
  FETCH_MODEL_RESPONSE,
  FETCH_MODELS_RESPONSE,
  ACCEPT_FIELD,
  REJECT_FIELD,
  ACCEPT_ALL_FIELDS,
  REJECT_ALL_FIELDS,
} from '#constants/actionTypes.js';

// Accept/reject flip a field's marking in FORM state instantly (local-first, UI design §3.4), and the
// change rides the model's ordinary debounced autosave. But the model page tears down and re-seeds the
// form from the ENTITY on poll/update-driven re-inits (Router unmount -> destroy(form) -> re-seed),
// and until the autosave round-trips the entity's formInputs still holds the OLD marking -- so a
// re-seed lands in that window and reverts the optimistic flip. Keep the entity's persisted marking in
// sync with the form flip here so any re-seed reads the current marking (data & API design §4.1).
// `markings` is [{ field, marking }, ...]; every field is applied to the current model in one pass so
// the byId map is cloned once, whatever the field count (a whole-model accept-all touches ~40 fields).
const markFields = (byId, current, markings) => {
  if (!current || !byId[current]) {
    return byId;
  }
  const model = markings.reduce((acc, { field, marking }) => {
    const existing = (acc.formInputs && acc.formInputs[field]) || {};
    return set(`formInputs.${field}`, { ...existing, ...marking }, acc);
  }, byId[current]);
  return { ...byId, [current]: model };
};

const rejectMarking = (field, autofillerFieldNames) => ({
  field,
  // marking cleared (aiStatus none, provenance null) and the value cleared; autofiller-backed
  // fields go back to autofilled so the form re-derives them (mirrors the form reducer).
  marking: {
    aiStatus: 'none',
    provenance: null,
    value: null,
    ...(autofillerFieldNames?.has?.(field) ? { autofilled: true } : {}),
  },
});

const byIdReducer = (state, action) => {
  const { meta: actionMeta, payload, type, error } = action;
  const { byId, current, meta } = state;
  switch (type) {
    case ACCEPT_FIELD: {
      // proposed -> accepted; value and provenance unchanged.
      return markFields(byId, current, [
        {
          field: actionMeta.field,
          marking: { aiStatus: 'accepted', provenance: payload.provenance },
        },
      ]);
    }
    case REJECT_FIELD: {
      return markFields(byId, current, [
        rejectMarking(actionMeta.field, payload.autofillerFieldNames),
      ]);
    }
    case ACCEPT_ALL_FIELDS: {
      return markFields(
        byId,
        current,
        payload.fields.map(({ name, provenance }) => ({
          field: name,
          marking: { aiStatus: 'accepted', provenance },
        }))
      );
    }
    case REJECT_ALL_FIELDS: {
      return markFields(
        byId,
        current,
        payload.fields.map(name => rejectMarking(name, payload.autofillerFieldNames))
      );
    }
    case FETCH_MODEL_RESPONSE: {
      if (error) return byId;
      const { requestId } = actionMeta;
      const { uuid } = payload;
      const modelMeta = meta.byId[uuid] || {};
      const modelValidFetchRequests = modelMeta.validFetchRequests || {};
      const isValid = modelValidFetchRequests[requestId];
      return !isValid || isEqual(byId[uuid], payload) ? byId : { ...byId, [uuid]: payload };
    }
    case FETCH_MODELS_RESPONSE: {
      if (error) return byId;
      const { requestId } = actionMeta;
      const { validFetchRequests } = meta.all;
      const isValid = validFetchRequests[requestId];
      const nextById = Object.values(payload.entities.models).reduce(
        (acc, model) => ({
          ...acc,
          [model.uuid]: model.uuid === current ? byId[model.uuid] : model,
        }),
        {}
      );

      return !isValid || isEqual(byId, nextById) ? byId : nextById;
    }
    default: {
      return byId;
    }
  }
};

export default byIdReducer;
